import { Fragment, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Text, Button, Badge, Dialog, DialogPanel, TextInput } from '@tremor/react';
import { api } from '../lib/api';
import { useSocket } from '../hooks/useSocket';
import { ErrorBanner } from '../components/ErrorBanner';
import type {
  SearchResultItem,
  SearchMatchMode,
  SearchExcludeMode,
  SearchExcludeFilter,
  SearchTermStatsResponse,
  SavedSearch,
  SavedSearchKind,
  BatchesResponse,
  VectaraCorpus,
  VectaraContentType,
  VectaraChunking,
  VectaraExportStatus,
} from '../types';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 500, 1000] as const;
const DEFAULT_PAGE_SIZE = 50;
const POLL_INTERVAL_MS = 3000;

type SearchPhase = 'idle' | 'restoring' | 'starting' | 'running' | 'paused' | 'cancelled' | 'complete' | 'failed';
type ResultsMode = 'live' | 'saved';

interface SavedViewMeta {
  id: string;
  name: string;
  matchCount: number;
  createdBy: SavedSearch['createdBy'];
  createdAt: string;
  kind: SavedSearchKind;
  autoSaved: boolean;
  refinedFromLabel: string | null;
  durationMs: number | null;
}

// Set when the user clicks "Refine" — the next run searches WITHIN the parent
// snapshot's results (scoped by the view filters captured at click time).
interface RefineDraft {
  parentSavedId: string;
  parentName: string;
  matchCount: number;
  filters: { excludeFilter: SearchExcludeFilter; requireTerms: string[] };
}

const STATUS_OPTIONS = ['completed', 'processing', 'failed', 'skipped'] as const;

function statusColor(status: string): 'emerald' | 'blue' | 'red' | 'yellow' | 'gray' | 'orange' {
  switch (status) {
    case 'completed': return 'emerald';
    case 'processing': return 'blue';
    case 'failed': return 'red';
    case 'pending': return 'yellow';
    case 'skipped': return 'orange';
    default: return 'gray';
  }
}

function formatGenre(genre: string | null): string {
  if (!genre) return '--';
  const special: Record<string, string> = { empty: 'Empty Document', error: 'Classification Error', unknown: 'Unknown' };
  return special[genre] || genre.replace(/_/g, ' ');
}

// ─── Results table columns ───
type ResultColumnKey = 'fileName' | 'batch' | 'status' | 'genre' | 'labels' | 'include' | 'exclude' | 'snippet';

interface ResultColumnConfig {
  key: ResultColumnKey;
  label: string;
  defaultVisible: boolean;
}

const RESULT_COLUMNS: ResultColumnConfig[] = [
  { key: 'fileName', label: 'Filename', defaultVisible: true },
  { key: 'batch', label: 'Batch', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'genre', label: 'Genre', defaultVisible: true },
  { key: 'labels', label: 'Labels', defaultVisible: true },
  { key: 'include', label: 'Matched include', defaultVisible: true },
  { key: 'exclude', label: 'Matched exclude', defaultVisible: true },
  { key: 'snippet', label: 'Snippet', defaultVisible: true },
];

const COLUMN_STORAGE_KEY = 'searchResultColumns';
const COLUMN_VERSION_KEY = 'searchResultColumnsVersion';
// Bump when adding new defaultVisible columns so they appear for existing users.
const COLUMN_VERSION = 1;

function defaultVisibleColumns(): Set<ResultColumnKey> {
  return new Set(RESULT_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key));
}

function loadVisibleColumns(): Set<ResultColumnKey> {
  try {
    const stored = localStorage.getItem(COLUMN_STORAGE_KEY);
    if (!stored) return defaultVisibleColumns();
    const known = new Set(RESULT_COLUMNS.map((c) => c.key));
    const parsed = (JSON.parse(stored) as ResultColumnKey[]).filter((k) => known.has(k));
    const columns = new Set(parsed);
    // Inject any newly-added defaultVisible columns since the saved version.
    const savedVersion = parseInt(localStorage.getItem(COLUMN_VERSION_KEY) || '1', 10);
    if (savedVersion < COLUMN_VERSION) {
      for (const col of RESULT_COLUMNS) {
        if (col.defaultVisible && !columns.has(col.key)) columns.add(col.key);
      }
      saveVisibleColumns(columns);
    }
    return columns.size > 0 ? columns : defaultVisibleColumns();
  } catch {
    return defaultVisibleColumns();
  }
}

function saveVisibleColumns(columns: Set<ResultColumnKey>): void {
  localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify([...columns]));
  localStorage.setItem(COLUMN_VERSION_KEY, String(COLUMN_VERSION));
}

// When on, the Snippet column is moved out of its truncated cell into a
// full-width row beneath each result so long matches are readable in full.
const SNIPPET_EXPAND_KEY = 'searchSnippetExpanded';

function loadSnippetExpanded(): boolean {
  try {
    return localStorage.getItem(SNIPPET_EXPAND_KEY) === 'true';
  } catch {
    return false;
  }
}

function saveSnippetExpanded(value: boolean): void {
  try {
    localStorage.setItem(SNIPPET_EXPAND_KEY, value ? 'true' : 'false');
  } catch {
    // ignore
  }
}

// ─── Per-session persistence (survives navigating to a document and back) ───
const STATE_STORAGE_KEY = 'advancedSearchState';

interface PersistedSearch {
  includeText: string;
  excludeText: string;
  batchNames: string[];
  statusFilter: string;
  matchMode: SearchMatchMode;
  excludeMode: SearchExcludeMode;
  wholeWord: boolean;
  mode: ResultsMode;
  searchId: string | null;
  savedId: string | null;
  resultsPage: number;
  pageSize: number;
  excludeFilter: SearchExcludeFilter;
  requiredTerms?: string[];
  refineDraft?: RefineDraft | null;
  /** Live Vectara export id — restored on mount so a long export survives navigation/reload. */
  vexportId?: string | null;
}

function loadPersistedSearch(): PersistedSearch | null {
  try {
    const raw = localStorage.getItem(STATE_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PersistedSearch) : null;
  } catch {
    return null;
  }
}

function savePersistedSearch(state: PersistedSearch): void {
  try {
    localStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore (quota / serialization)
  }
}

function formatSavedBy(createdBy: SavedSearch['createdBy']): string {
  if (!createdBy) return 'unknown';
  return createdBy.name || createdBy.email || 'unknown';
}

/** "42s" / "12m 4s" / "2h 18m" — for scan/export durations. */
function formatDurationMs(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min < 60) return sec ? `${min}m ${sec}s` : `${min}m`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return remMin ? `${hr}h ${remMin}m` : `${hr}h`;
}

export function AdvancedSearchPage() {
  const navigate = useNavigate();
  const { subscribe } = useSocket();

  // Restored once per mount (so browser-back rehydrates the previous search).
  const [persisted] = useState(loadPersistedSearch);
  const willRestore = !!persisted && (
    (persisted.mode === 'saved' && !!persisted.savedId) || !!persisted.searchId
  );

  // ─── Form inputs ───
  const [includeText, setIncludeText] = useState(persisted?.includeText ?? '');
  const [excludeText, setExcludeText] = useState(persisted?.excludeText ?? '');
  const [batches, setBatches] = useState<string[]>([]);
  const [selectedBatches, setSelectedBatches] = useState<Set<string>>(new Set(persisted?.batchNames ?? []));
  const [statusFilter, setStatusFilter] = useState<string>(persisted?.statusFilter ?? 'completed');
  const [matchMode, setMatchMode] = useState<SearchMatchMode>(persisted?.matchMode ?? 'any');
  const [excludeMode, setExcludeMode] = useState<SearchExcludeMode>(persisted?.excludeMode ?? 'exclude');
  const [wholeWord, setWholeWord] = useState(persisted?.wholeWord ?? true);

  // ─── Lifecycle ───
  const [phase, setPhase] = useState<SearchPhase>(willRestore ? 'restoring' : 'idle');
  const [mode, setMode] = useState<ResultsMode>(persisted?.mode ?? 'live');
  const [searchId, setSearchId] = useState<string | null>(persisted?.searchId ?? null);
  const [searchStale, setSearchStale] = useState(false);
  const [controlBusy, setControlBusy] = useState(false);
  const [progress, setProgress] = useState({ processed: 0, total: 0, matchCount: 0 });
  // Active scan time (ms) from the server — live while running, final on completion.
  const [searchDurationMs, setSearchDurationMs] = useState<number | null>(null);
  const [cappedAt, setCappedAt] = useState<number | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ─── Saved-search view + sharing ───
  const [savedView, setSavedView] = useState<SavedViewMeta | null>(null);
  const [rerunOfSaved, setRerunOfSaved] = useState<{ id: string; name: string } | null>(null);
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ─── Results + pagination ───
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [resultsTotal, setResultsTotal] = useState(0);
  const [resultsPage, setResultsPage] = useState(persisted?.resultsPage ?? 1);
  const [pageSize, setPageSize] = useState<number>(persisted?.pageSize ?? DEFAULT_PAGE_SIZE);
  const [excludeFilter, setExcludeFilter] = useState<SearchExcludeFilter>(persisted?.excludeFilter ?? 'all');
  const [excludeMatchTotal, setExcludeMatchTotal] = useState(0);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // ─── Found-term filter (instant, operates on stored results only) ───
  const [requiredTerms, setRequiredTerms] = useState<string[]>(persisted?.requiredTerms ?? []);
  const [termStats, setTermStats] = useState<SearchTermStatsResponse | null>(null);
  const [materializing, setMaterializing] = useState(false);

  // ─── Refinement chain ───
  const [refineDraft, setRefineDraft] = useState<RefineDraft | null>(persisted?.refineDraft ?? null);
  const [refinePreparing, setRefinePreparing] = useState(false);
  // Saved snapshot corresponding to the current LIVE results (the auto-saved
  // refinement step, or the ensure-saved root) — anchors the chain breadcrumb.
  const [autoSavedId, setAutoSavedId] = useState<string | null>(null);
  const [autoSaveError, setAutoSaveError] = useState<string | null>(null);

  const changePageSize = useCallback((size: number) => {
    setPageSize(size);
    setResultsPage(1);
  }, []);

  const changeExcludeFilter = useCallback((value: SearchExcludeFilter) => {
    setExcludeFilter(value);
    setResultsPage(1);
  }, []);

  const toggleRequiredTerm = useCallback((term: string) => {
    setRequiredTerms((prev) => (
      prev.includes(term) ? prev.filter((t) => t !== term) : [...prev, term]
    ));
    setResultsPage(1);
  }, []);

  const clearRequiredTerms = useCallback(() => {
    setRequiredTerms([]);
    setResultsPage(1);
  }, []);

  // ─── Column visibility ───
  const [visibleColumns, setVisibleColumns] = useState<Set<ResultColumnKey>>(loadVisibleColumns);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [snippetExpanded, setSnippetExpanded] = useState<boolean>(loadSnippetExpanded);
  const columnPickerRef = useRef<HTMLDivElement>(null);
  // Focused after clicking Refine (the form scrolls back into view emptied).
  const includeTextareaRef = useRef<HTMLTextAreaElement>(null);

  const isColumnVisible = (key: ResultColumnKey) => visibleColumns.has(key);
  const toggleSnippetExpanded = useCallback(() => {
    setSnippetExpanded((v) => {
      const next = !v;
      saveSnippetExpanded(next);
      return next;
    });
  }, []);
  const toggleColumn = useCallback((key: ResultColumnKey) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      saveVisibleColumns(next);
      return next;
    });
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (columnPickerRef.current && !columnPickerRef.current.contains(event.target as Node)) {
        setShowColumnPicker(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ─── Selection (for Vectara export) ───
  const [vectaraConfigured, setVectaraConfigured] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [allIds, setAllIds] = useState<string[]>([]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const selectAll = useCallback(() => setSelectedIds(new Set(allIds)), [allIds]);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // ─── Vectara export modal + progress ───
  const [showVectaraModal, setShowVectaraModal] = useState(false);
  const [corpora, setCorpora] = useState<VectaraCorpus[]>([]);
  const [corporaLoading, setCorporaLoading] = useState(false);
  const [corpusMode, setCorpusMode] = useState<'existing' | 'new'>('existing');
  const [selectedCorpusKey, setSelectedCorpusKey] = useState('');
  const [newCorpus, setNewCorpus] = useState({ key: '', name: '', description: '' });
  const [vContentType, setVContentType] = useState<VectaraContentType>('text');
  const [vChunking, setVChunking] = useState<VectaraChunking>('sentence');
  const [vOverwrite, setVOverwrite] = useState(true);
  const [vStarting, setVStarting] = useState(false);
  const [vModalError, setVModalError] = useState<string | null>(null);
  const [vexportId, setVexportId] = useState<string | null>(null);
  const [vexportPhase, setVexportPhase] = useState<'idle' | 'running' | 'complete' | 'failed' | 'cancelled'>('idle');
  const [vexportStatus, setVexportStatus] = useState<VectaraExportStatus | null>(null);
  // Server heartbeat went quiet mid-export (pod restart/deploy) — offer Resume.
  const [vexportStale, setVexportStale] = useState(false);

  const includeTerms = useMemo(
    () => includeText.split('\n').map((s) => s.trim()).filter(Boolean),
    [includeText],
  );
  const excludeTerms = useMemo(
    () => excludeText.split('\n').map((s) => s.trim()).filter(Boolean),
    [excludeText],
  );

  const isRunning = phase === 'starting' || phase === 'running';
  const isBusy = isRunning || phase === 'restoring';
  // When refining, the corpus is the parent snapshot — no batch selection needed.
  const canRun = includeTerms.length > 0 && (refineDraft ? true : selectedBatches.size > 0) && !isBusy;
  const hasResults = phase === 'complete' || phase === 'paused' || phase === 'cancelled';
  const totalPages = Math.max(1, Math.ceil(resultsTotal / pageSize));

  // ─── Load batch list once ───
  useEffect(() => {
    api.getBatches()
      .then((response: BatchesResponse) => {
        setBatches(
          response.batches
            .map((b) => b.name)
            .filter((name): name is string => name !== null && name !== 'Unknown'),
        );
      })
      .catch(() => {
        // ignore — batch picker just stays empty
      });
  }, []);

  // ─── Run (optionally as a re-run of a saved search) ───
  const handleRun = useCallback(async (rerunOf?: { id: string; name: string } | null) => {
    setMode('live');
    setSavedView(null);
    setRerunOfSaved(rerunOf ?? null);
    setSearchId(null);
    setPhase('starting');
    setError(null);
    setWarning(null);
    setCappedAt(null);
    setResults([]);
    setResultsTotal(0);
    setResultsPage(1);
    setAllIds([]);
    setSelectedIds(new Set());
    setRequiredTerms([]);
    setTermStats(null);
    setAutoSavedId(null);
    setAutoSaveError(null);
    setProgress({ processed: 0, total: 0, matchCount: 0 });
    setSearchDurationMs(null);
    try {
      const res = await api.startSearch({
        includeTerms: includeText,
        excludeTerms: excludeText,
        batchNames: refineDraft ? [] : [...selectedBatches],
        status: statusFilter || undefined,
        matchMode,
        excludeMode,
        wholeWord,
        ...(refineDraft
          ? { refineFromSavedId: refineDraft.parentSavedId, refineFilters: refineDraft.filters }
          : {}),
      });
      setSearchId(res.searchId);
      setProgress((p) => ({ ...p, total: res.totalDocuments }));
      if (res.warning) setWarning(res.warning);
      setPhase('running');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start search');
      setPhase('failed');
    }
  }, [includeText, excludeText, selectedBatches, statusFilter, matchMode, excludeMode, wholeWord, refineDraft]);

  // ─── Socket subscriptions (guarded by searchId) ───
  useEffect(() => {
    if (!searchId) return;
    const unsubP = subscribe('search:progress', (d) => {
      if (d.searchId !== searchId) return;
      setProgress({ processed: d.processedDocuments, total: d.totalDocuments, matchCount: d.matchCount });
      setCappedAt(d.cappedAt);
    });
    const unsubC = subscribe('search:complete', (d) => {
      if (d.searchId !== searchId) return;
      setProgress({ processed: d.processedDocuments, total: d.totalDocuments, matchCount: d.matchCount });
      setCappedAt(d.cappedAt);
      if (typeof d.durationMs === 'number') setSearchDurationMs(d.durationMs);
      if (d.savedId) { setAutoSavedId(d.savedId); setRefineDraft(null); } // refinement auto-saved as a chain step
      if (d.autoSaveError) setAutoSaveError(d.autoSaveError);
      setPhase('complete');
    });
    const unsubF = subscribe('search:failed', (d) => {
      if (d.searchId !== searchId) return;
      setError(d.error);
      setPhase('failed');
    });
    const unsubPause = subscribe('search:paused', (d) => {
      if (d.searchId !== searchId) return;
      setSearchStale(false);
      if (typeof d.durationMs === 'number') setSearchDurationMs(d.durationMs);
      setPhase('paused');
    });
    const unsubCancel = subscribe('search:cancelled', (d) => {
      if (d.searchId !== searchId) return;
      setSearchStale(false);
      if (typeof d.durationMs === 'number') setSearchDurationMs(d.durationMs);
      setPhase('cancelled');
    });
    return () => { unsubP(); unsubC(); unsubF(); unsubPause(); unsubCancel(); };
  }, [subscribe, searchId]);

  // ─── Polling fallback (robust to a disconnected socket) ───
  useEffect(() => {
    if (phase !== 'running' || !searchId) return;
    const id = setInterval(async () => {
      try {
        const s = await api.getSearchStatus(searchId);
        setProgress({ processed: s.processedDocuments, total: s.totalDocuments, matchCount: s.matchCount });
        setCappedAt(s.cappedAt);
        if (typeof s.durationMs === 'number') setSearchDurationMs(s.durationMs);
        if (s.status === 'completed') {
          if (s.savedId) { setAutoSavedId(s.savedId); setRefineDraft(null); }
          if (s.autoSaveError) setAutoSaveError(s.autoSaveError);
          setPhase('complete');
        }
        else if (s.status === 'failed') { setError(s.error || 'Search failed'); setPhase('failed'); }
        else if (s.status === 'paused') setPhase('paused');
        else if (s.status === 'cancelled') setPhase('cancelled');
        else setSearchStale(!!s.stale); // still processing — flag a stalled runner
      } catch {
        // transient — keep polling
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [phase, searchId]);

  // ─── Fetch a results page once complete/paused/cancelled (live or saved) ───
  useEffect(() => {
    if (!(phase === 'complete' || phase === 'paused' || phase === 'cancelled')) return;
    const sourceId = mode === 'saved' ? savedView?.id : searchId;
    if (!sourceId) return;
    let cancelled = false;
    setResultsLoading(true);
    const skip = (resultsPage - 1) * pageSize;
    const opts = { limit: pageSize, skip, excludeFilter, requireTerms: requiredTerms };
    const req = mode === 'saved'
      ? api.getSavedSearchResults(sourceId, opts)
      : api.getSearchResults(sourceId, opts);
    req
      .then((r) => {
        if (cancelled) return;
        setResults(r.results);
        setResultsTotal(r.pagination.total);
        setCappedAt(r.cappedAt);
        setExcludeMatchTotal(r.excludeMatchTotal ?? 0);
      })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load results'); })
      .finally(() => { if (!cancelled) setResultsLoading(false); });
    return () => { cancelled = true; };
  }, [phase, mode, searchId, savedView?.id, resultsPage, pageSize, excludeFilter, requiredTerms]);

  // ─── Term stats for the found-term filter chips (re-fetched on pause/cancel too) ───
  useEffect(() => {
    if (!(phase === 'complete' || phase === 'paused' || phase === 'cancelled')) return;
    const sourceId = mode === 'saved' ? savedView?.id : searchId;
    if (!sourceId) return;
    let cancelled = false;
    const req = mode === 'saved'
      ? api.getSavedSearchTermStats(sourceId)
      : api.getSearchTermStats(sourceId);
    req
      .then((r) => { if (!cancelled) setTermStats(r); })
      .catch(() => { if (!cancelled) setTermStats(null); }); // chips just don't render
    return () => { cancelled = true; };
  }, [phase, mode, searchId, savedView?.id]);

  // ─── Vectara availability probe (once) ───
  useEffect(() => {
    api.getVectaraConfig().then((r) => setVectaraConfigured(r.configured)).catch(() => setVectaraConfigured(false));
  }, []);

  // ─── Fetch the full id universe (for select-all across pages); default all selected ───
  useEffect(() => {
    if (!vectaraConfigured || !(phase === 'complete' || phase === 'paused' || phase === 'cancelled')) return;
    const sourceId = mode === 'saved' ? savedView?.id : searchId;
    if (!sourceId) return;
    let cancelled = false;
    const req = mode === 'saved'
      ? api.getSavedSearchResultIds(sourceId, { excludeFilter, requireTerms: requiredTerms })
      : api.getSearchResultIds(sourceId, { excludeFilter, requireTerms: requiredTerms });
    req
      .then((r) => {
        if (cancelled) return;
        setAllIds(r.documentIds);
        setSelectedIds(new Set(r.documentIds)); // default: all selected (reset on filter/source change)
      })
      .catch(() => { if (!cancelled) { setAllIds([]); setSelectedIds(new Set()); } });
    return () => { cancelled = true; };
  }, [vectaraConfigured, phase, mode, searchId, savedView?.id, excludeFilter, requiredTerms]);

  // ─── Export (live or saved snapshot) ───
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      // Export what's on screen: the active exclude + found-term filters apply.
      const opts = { excludeFilter, requireTerms: requiredTerms };
      if (mode === 'saved' && savedView) await api.downloadSavedSearchResults(savedView.id, opts);
      else if (searchId) await api.downloadSearchResults(searchId, opts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [mode, savedView, searchId, excludeFilter, requiredTerms]);

  // ─── Vectara export: open modal (loads corpora), start, live progress ───
  const openVectaraModal = useCallback(() => {
    // An export is live (or interrupted) — reopen the progress view, don't reset.
    if (vexportId && (vexportPhase === 'running' || vexportStale)) {
      setShowVectaraModal(true);
      return;
    }
    setVModalError(null);
    setVexportPhase('idle');
    setVexportId(null);
    setVexportStatus(null);
    setVexportStale(false);
    setCorpusMode('existing');
    setSelectedCorpusKey('');
    setNewCorpus({ key: '', name: '', description: '' });
    setVContentType('text');
    setVChunking('sentence');
    setVOverwrite(true);
    setShowVectaraModal(true);
    setCorporaLoading(true);
    api.listVectaraCorpora()
      .then((r) => { setCorpora(r.corpora); if (r.corpora.length) setSelectedCorpusKey(r.corpora[0].key); })
      .catch((e) => setVModalError(e instanceof Error ? e.message : 'Failed to load corpora'))
      .finally(() => setCorporaLoading(false));
  }, [vexportId, vexportPhase, vexportStale]);

  const handleStartVectaraExport = useCallback(async () => {
    setVStarting(true);
    setVModalError(null);
    try {
      let corpusKey = selectedCorpusKey;
      if (corpusMode === 'new') {
        const key = newCorpus.key.trim();
        if (!key) { setVModalError('Corpus key is required'); return; }
        const created = await api.createVectaraCorpus({
          key, name: newCorpus.name.trim() || key, description: newCorpus.description.trim() || undefined,
        });
        corpusKey = created.key;
      }
      if (!corpusKey) { setVModalError('Choose or create a corpus'); return; }
      const { exportId } = await api.startVectaraExport({
        documentIds: [...selectedIds],
        corpusKey,
        contentType: vContentType,
        overwrite: vOverwrite,
        chunking: vChunking,
      });
      setVexportId(exportId);
      setVexportStatus({ exportId, status: 'processing', total: selectedIds.size, processed: 0, succeeded: 0, failed: 0, skipped: 0, error: null });
      setVexportStale(false);
      setVexportPhase('running');
    } catch (e) {
      setVModalError(e instanceof Error ? e.message : 'Failed to start export');
    } finally {
      setVStarting(false);
    }
  }, [corpusMode, selectedCorpusKey, newCorpus, selectedIds, vContentType, vOverwrite, vChunking]);

  const handleVectaraCancel = useCallback(async () => {
    if (!vexportId) return;
    if (!window.confirm('Cancel this export? Documents already indexed stay in the corpus.')) return;
    try { await api.cancelVectaraExport(vexportId); }
    catch (e) { setVModalError(e instanceof Error ? e.message : 'Failed to cancel export'); }
  }, [vexportId]);

  const handleVectaraResume = useCallback(async () => {
    if (!vexportId) return;
    setVModalError(null);
    try {
      await api.resumeVectaraExport(vexportId);
      setVexportStale(false);
      setVexportPhase('running');
    } catch (e) {
      setVModalError(e instanceof Error ? e.message : 'Failed to resume export');
    }
  }, [vexportId]);

  // Vectara progress over socket (guarded by exportId)
  useEffect(() => {
    if (!vexportId) return;
    const unsubP = subscribe('vectara:progress', (d) => {
      if (d.exportId !== vexportId) return;
      setVexportStatus((prev) => ({ ...(prev ?? {}), ...d } as VectaraExportStatus));
    });
    const unsubC = subscribe('vectara:complete', (d) => {
      if (d.exportId !== vexportId) return;
      setVexportStatus((prev) => ({ ...(prev ?? {}), ...d } as VectaraExportStatus));
      setVexportPhase('complete');
    });
    const unsubF = subscribe('vectara:failed', (d) => {
      if (d.exportId !== vexportId) return;
      setVexportStatus((prev) => (prev ? { ...prev, status: 'failed', error: d.error } : prev));
      setVexportPhase('failed');
    });
    const unsubX = subscribe('vectara:cancelled', (d) => {
      if (d.exportId !== vexportId) return;
      setVexportStatus((prev) => ({ ...(prev ?? {}), ...d } as VectaraExportStatus));
      setVexportPhase('cancelled');
    });
    return () => { unsubP(); unsubC(); unsubF(); unsubX(); };
  }, [subscribe, vexportId]);

  // Polling fallback while a Vectara export runs. Also detects interruption:
  // the status turns `stale` when the server-side heartbeat goes quiet
  // (pod restart/deploy mid-export) — surfaced as a Resume offer.
  useEffect(() => {
    if (vexportPhase !== 'running' || !vexportId) return;
    const id = setInterval(async () => {
      try {
        const s = await api.getVectaraExportStatus(vexportId);
        setVexportStatus(s);
        setVexportStale(!!s.stale);
        if (s.status === 'completed') setVexportPhase('complete');
        else if (s.status === 'failed') setVexportPhase('failed');
        else if (s.status === 'cancelled') setVexportPhase('cancelled');
      } catch { /* keep polling */ }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [vexportPhase, vexportId]);

  // ─── Saved searches: list, open snapshot, save, refresh, delete ───
  const loadSavedSearches = useCallback(async () => {
    try {
      const r = await api.listSavedSearches();
      setSavedSearches(r.savedSearches);
    } catch {
      // ignore — panel just stays empty
    }
  }, []);

  useEffect(() => { loadSavedSearches(); }, [loadSavedSearches]);

  const openSaved = useCallback(async (id: string) => {
    setPhase('restoring');
    setError(null);
    setWarning(null);
    setRerunOfSaved(null);
    setAllIds([]);
    setSelectedIds(new Set());
    setRequiredTerms([]);
    setTermStats(null);
    try {
      const saved = await api.getSavedSearch(id);
      setIncludeText(saved.query.includeTerms.join('\n'));
      setExcludeText(saved.query.excludeTerms.join('\n'));
      setSelectedBatches(new Set(saved.query.batchNames));
      setStatusFilter(saved.query.status || 'completed');
      setMatchMode(saved.query.matchMode);
      setExcludeMode(saved.query.excludeMode);
      setWholeWord(!!saved.query.wholeWord);
      setSavedView({
        id: saved.id, name: saved.name, matchCount: saved.matchCount,
        createdBy: saved.createdBy, createdAt: saved.createdAt,
        kind: saved.kind || 'search', autoSaved: !!saved.autoSaved,
        refinedFromLabel: saved.refinedFrom?.label ?? null,
        durationMs: saved.durationMs ?? null,
      });
      setCappedAt(saved.cappedAt);
      setMode('saved');
      setSearchId(null);
      setResultsPage(1);
      setPhase('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open saved search');
      setMode('live');
      setSavedView(null);
      setPhase('idle');
    }
  }, []);

  const rehydrateLive = useCallback(async (id: string) => {
    setMode('live');
    setSearchId(id);
    setPhase('restoring');
    try {
      const s = await api.getSearchStatus(id);
      setProgress({ processed: s.processedDocuments, total: s.totalDocuments, matchCount: s.matchCount });
      setCappedAt(s.cappedAt);
      setSearchDurationMs(typeof s.durationMs === 'number' ? s.durationMs : null);
      if (s.savedId) setAutoSavedId(s.savedId);
      if (s.autoSaveError) setAutoSaveError(s.autoSaveError);
      if (s.status === 'completed') setPhase('complete');
      else if (s.status === 'failed') { setError(s.error || 'Search failed'); setPhase('failed'); }
      else if (s.status === 'paused') setPhase('paused');
      else if (s.status === 'cancelled') setPhase('cancelled');
      else { setSearchStale(!!s.stale); setPhase('running'); }
    } catch {
      // expired / not found — drop the stale id, keep the form filled
      setSearchId(null);
      setPhase('idle');
    }
  }, []);

  // ─── Job control ───
  const handlePause = useCallback(async () => {
    if (!searchId) return;
    setControlBusy(true);
    try { await api.pauseSearch(searchId); }
    catch (err) { setError(err instanceof Error ? err.message : 'Failed to pause'); }
    finally { setControlBusy(false); }
  }, [searchId]);

  const handleResume = useCallback(async () => {
    if (!searchId) return;
    setControlBusy(true);
    setSearchStale(false);
    try { await api.resumeSearch(searchId); setPhase('running'); }
    catch (err) { setError(err instanceof Error ? err.message : 'Failed to resume'); }
    finally { setControlBusy(false); }
  }, [searchId]);

  const handleCancel = useCallback(async () => {
    if (!searchId) return;
    if (!window.confirm('Cancel this search? Documents found so far are kept.')) return;
    setControlBusy(true);
    try { await api.cancelSearch(searchId); }
    catch (err) { setError(err instanceof Error ? err.message : 'Failed to cancel'); }
    finally { setControlBusy(false); }
  }, [searchId]);

  const handleSave = useCallback(async () => {
    if (!searchId || !saveName.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.saveSearch(searchId, { name: saveName.trim(), description: saveDescription.trim() || undefined });
      setShowSaveModal(false);
      setSaveName('');
      setSaveDescription('');
      await loadSavedSearches();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save search');
    } finally {
      setSaving(false);
    }
  }, [searchId, saveName, saveDescription, loadSavedSearches]);

  const handleUpdateSaved = useCallback(async () => {
    if (!searchId || !rerunOfSaved) return;
    setSaving(true);
    try {
      await api.refreshSavedSearch(rerunOfSaved.id, searchId);
      setRerunOfSaved(null);
      loadSavedSearches();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update saved search');
    } finally {
      setSaving(false);
    }
  }, [searchId, rerunOfSaved, loadSavedSearches]);

  const handleDeleteSaved = useCallback(async (id: string, name: string) => {
    if (!window.confirm(`Delete saved search "${name}"? This removes it for all admins.`)) return;
    try {
      await api.deleteSavedSearch(id);
      if (savedView?.id === id) {
        setMode('live');
        setSavedView(null);
        setPhase('idle');
      }
      loadSavedSearches();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete saved search');
    }
  }, [savedView, loadSavedSearches]);

  // ─── Refinement: search within the current result set ───
  // Ensures the parent is a durable snapshot first (chains never expire), then
  // switches the form into refine mode with the current view filters captured.
  const startRefine = useCallback(async () => {
    setRefinePreparing(true);
    setError(null);
    try {
      let parentId: string; let parentName: string; let parentCount: number;
      if (mode === 'saved' && savedView) {
        parentId = savedView.id; parentName = savedView.name; parentCount = savedView.matchCount;
      } else if (searchId) {
        const parent = await api.ensureSearchSaved(searchId);
        parentId = parent.id; parentName = parent.name; parentCount = parent.matchCount;
        setAutoSavedId(parent.id);
        loadSavedSearches();
      } else {
        return;
      }
      setRefineDraft({
        parentSavedId: parentId,
        parentName,
        matchCount: parentCount,
        filters: { excludeFilter, requireTerms: requiredTerms },
      });
      setIncludeText('');
      setExcludeText('');
      // The refine banner + emptied term box are at the top of the page — take
      // the user there so the next step (typing narrower terms) is obvious.
      window.scrollTo({ top: 0, behavior: 'smooth' });
      includeTextareaRef.current?.focus({ preventScroll: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to prepare refinement');
    } finally {
      setRefinePreparing(false);
    }
  }, [mode, savedView, searchId, excludeFilter, requiredTerms, loadSavedSearches]);

  // Materialize the active view filters as a durable chain step (no re-scan).
  const handleMaterialize = useCallback(async () => {
    setMaterializing(true);
    setError(null);
    try {
      const body = { requireTerms: requiredTerms, excludeFilter };
      const saved = mode === 'saved' && savedView
        ? await api.deriveSavedSearch(savedView.id, body)
        : searchId ? await api.deriveSearch(searchId, body) : null;
      if (saved) {
        await loadSavedSearches();
        await openSaved(saved.id); // jump to the new step (clears the chip filter)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to materialize the filtered set');
    } finally {
      setMaterializing(false);
    }
  }, [mode, savedView, searchId, requiredTerms, excludeFilter, loadSavedSearches, openSaved]);

  // Keep the saved panel in sync when a refinement auto-saves.
  useEffect(() => {
    if (autoSavedId) loadSavedSearches();
  }, [autoSavedId, loadSavedSearches]);

  // The breadcrumb path for the step being viewed: walk refinedFrom links from
  // the current snapshot up to the chain root. All steps are durable SavedSearches.
  const chainSteps = useMemo(() => {
    const currentId = mode === 'saved' ? savedView?.id : autoSavedId;
    if (!currentId) return [] as SavedSearch[];
    const byId = new Map(savedSearches.map((s) => [s.id, s]));
    const path: SavedSearch[] = [];
    const seen = new Set<string>();
    let node = byId.get(currentId);
    while (node && !seen.has(node.id)) {
      seen.add(node.id);
      path.unshift(node);
      node = node.refinedFrom?.savedId ? byId.get(node.refinedFrom.savedId) : undefined;
    }
    return path;
  }, [savedSearches, mode, savedView?.id, autoSavedId]);

  // Saved panel grouping: one block per chain, root first, steps indented.
  const savedGroups = useMemo(() => {
    const groups = new Map<string, SavedSearch[]>();
    for (const s of savedSearches) {
      const key = s.chainId || s.id;
      const list = groups.get(key);
      if (list) list.push(s); else groups.set(key, [s]);
    }
    const out: { root: SavedSearch; steps: SavedSearch[] }[] = [];
    for (const items of groups.values()) {
      items.sort((a, b) => (a.stepIndex - b.stepIndex) || a.createdAt.localeCompare(b.createdAt));
      out.push({ root: items[0], steps: items.slice(1) });
    }
    out.sort((a, b) => b.root.createdAt.localeCompare(a.root.createdAt));
    return out;
  }, [savedSearches]);

  // ─── Restore previous search once on mount (browser-back continuity) ───
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    if (!persisted) return;
    if (persisted.mode === 'saved' && persisted.savedId) openSaved(persisted.savedId);
    else if (persisted.searchId) rehydrateLive(persisted.searchId);
    // A Vectara export runs server-side — re-attach to it (live or interrupted)
    // so navigation/reload doesn't lose sight of a multi-hour export.
    if (persisted.vexportId) {
      const id = persisted.vexportId;
      api.getVectaraExportStatus(id)
        .then((s) => {
          if (s.status === 'processing' || s.status === 'pending') {
            setVexportId(id);
            setVexportStatus(s);
            setVexportStale(!!s.stale);
            setVexportPhase('running');
          }
          // terminal → drop; the persist effect clears the stored id
        })
        .catch(() => { /* expired / not found — drop */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Persist enough to rehydrate after navigating away ───
  useEffect(() => {
    savePersistedSearch({
      includeText,
      excludeText,
      batchNames: [...selectedBatches],
      statusFilter,
      matchMode,
      excludeMode,
      wholeWord,
      mode,
      searchId,
      savedId: savedView?.id ?? null,
      resultsPage,
      pageSize,
      excludeFilter,
      requiredTerms,
      refineDraft,
      vexportId: vexportPhase === 'running' ? vexportId : null,
    });
  }, [includeText, excludeText, selectedBatches, statusFilter, matchMode, excludeMode, wholeWord, mode, searchId, savedView, resultsPage, pageSize, excludeFilter, requiredTerms, refineDraft, vexportId, vexportPhase]);

  // ─── Row click → detail page (with Prev/Next nav list) ───
  const openDocument = useCallback((documentId: string, newTab: boolean) => {
    const href = `/documents/${encodeURIComponent(documentId)}`;
    if (newTab) {
      window.open(href, '_blank', 'noopener');
      return;
    }
    // Same-tab SPA nav — store the id list so the detail page's Prev/Next works.
    const ids = results.map((r) => r.documentId);
    sessionStorage.setItem('documentNavList', JSON.stringify(ids));
    sessionStorage.setItem('documentNavIndex', String(ids.indexOf(documentId)));
    navigate(href);
  }, [results, navigate]);

  // Left-click → same tab; ⌘/Ctrl/Shift-click → new tab/window.
  const handleRowClick = useCallback((e: React.MouseEvent, documentId: string) => {
    openDocument(documentId, e.metaKey || e.ctrlKey || e.shiftKey);
  }, [openDocument]);

  // Middle-click → new tab (suppress the default autoscroll on mousedown).
  const handleRowAuxClick = useCallback((e: React.MouseEvent, documentId: string) => {
    if (e.button === 1) {
      e.preventDefault();
      openDocument(documentId, true);
    }
  }, [openDocument]);
  const handleRowMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 1) e.preventDefault();
  }, []);

  const pct = progress.total > 0 ? Math.min(100, Math.round((progress.processed / progress.total) * 100)) : 0;

  // When the snippet is expanded it renders as a full-width row below each
  // result, so it leaves the inline column set.
  const snippetAsRow = isColumnVisible('snippet') && snippetExpanded;
  const inlineColumns = RESULT_COLUMNS.filter(
    (c) => isColumnVisible(c.key) && !(c.key === 'snippet' && snippetExpanded),
  );
  const inlineColumnCount = Math.max(1, inlineColumns.length);

  // Selection (Vectara export) — leading checkbox column when configured.
  const selectionEnabled = vectaraConfigured;
  const totalColSpan = inlineColumnCount + (selectionEnabled ? 1 : 0);
  const allSelected = allIds.length > 0 && selectedIds.size === allIds.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  return (
    <div className="max-w-[1600px] mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-100">Advanced Search</h1>
        <Text className="text-gray-400 mt-1">
          Search document text across one or more batches for a list of terms.
        </Text>
      </div>

      {/* Inline errors that occur outside the failed phase (export / results / delete) */}
      {error && phase !== 'failed' && (
        <div className="flex items-start justify-between gap-3 rounded ring-1 ring-rose-800 bg-rose-950/40 px-3 py-2 text-sm text-rose-200">
          <span><span className="font-semibold">Something went wrong.</span>{' '}<span className="text-rose-300/90">{error}</span></span>
          <button type="button" onClick={() => setError(null)} className="text-rose-300/70 hover:text-rose-100 shrink-0">✕</button>
        </div>
      )}

      {/* ─── Query builder ─── */}
      <Card className="bg-gray-900 ring-gray-800">
        {refineDraft && (
          <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-amber-800 bg-amber-950/40 px-4 py-3 mb-4">
            <div className="text-sm text-amber-200">
              <span className="font-semibold">Refining “{refineDraft.parentName}”</span>
              {' '}— the next run searches only that snapshot’s {refineDraft.matchCount.toLocaleString()} documents.
              {(refineDraft.filters.requireTerms.length > 0 || refineDraft.filters.excludeFilter !== 'all') && (
                <span className="block text-amber-300/80 mt-0.5">
                  Scoped to the view filters captured when you clicked Refine
                  {refineDraft.filters.requireTerms.length > 0 && <> · requires: {refineDraft.filters.requireTerms.join(', ')}</>}
                  {refineDraft.filters.excludeFilter === 'without' && <> · rows with exclude hits removed</>}
                  {refineDraft.filters.excludeFilter === 'with' && <> · only rows with exclude hits</>}
                </span>
              )}
              <span className="block text-amber-300/60 mt-0.5">Each completed refinement is auto-saved as a step you can jump back to.</span>
            </div>
            <button
              type="button"
              onClick={() => setRefineDraft(null)}
              className="text-amber-300/70 hover:text-amber-100 text-sm shrink-0"
            >
              ✕ Cancel refine
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Include terms <span className="text-gray-500 font-normal">(one per line — use &quot;&nbsp;+&nbsp;&quot; to require multiple phrases in the same document)</span>
              <InfoTip text='Each line is a separate term or phrase, matched case-insensitively anywhere in a document&apos;s text. A document is a result when at least one line is found (or every line, with "Match ALL"). Join phrases on one line with a spaced plus — "stanton square + crime" — to require all of them in the same document.' />
            </label>
            <textarea
              ref={includeTextareaRef}
              value={includeText}
              onChange={(e) => setIncludeText(e.target.value)}
              rows={10}
              placeholder={'discrimination\nredlining\nrestrictive covenant + sale'}
              className="w-full bg-gray-950 border border-gray-800 rounded-lg p-3 text-gray-100 text-sm font-mono resize-y focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <Text className="text-gray-500 text-xs mt-1">
              {includeTerms.length} terms · a plus with spaces ANDs phrases on one line (terms like C++ are unaffected)
            </Text>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Exclude / downrank terms <span className="text-gray-500 font-normal">(one per line — &quot;&nbsp;+&nbsp;&quot; works here too)</span>
              <InfoTip text='Documents containing any of these terms are dropped from the results ("Hide matches") or kept but marked ("Flag only") so you can filter them in the results view. Useful for boilerplate like disclaimers.' />
            </label>
            <textarea
              value={excludeText}
              onChange={(e) => setExcludeText(e.target.value)}
              rows={10}
              placeholder={'disclaimer\nboilerplate'}
              className="w-full bg-gray-950 border border-gray-800 rounded-lg p-3 text-gray-100 text-sm font-mono resize-y focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <Text className="text-gray-500 text-xs mt-1">{excludeTerms.length} terms</Text>
          </div>
        </div>

        {/* Batch picker (hidden when refining — the corpus is the parent snapshot) */}
        {!refineDraft && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Batches to search
              <InfoTip text="The set of documents to scan: every processed document in the selected import batches. The search reads each document's extracted text, so large selections take longer." />
            </label>
            <BatchMultiSelect
              batches={batches}
              selected={selectedBatches}
              onChange={setSelectedBatches}
            />
          </div>
        )}

        {/* Options */}
        <div className="flex flex-wrap items-end gap-x-8 gap-y-4 mt-4">
          <Segmented
            label="Include match"
            info="Match ANY: a document is a result if at least one include line is found in it. Match ALL: every include line must be found somewhere in the document — much stricter."
            value={matchMode}
            options={[
              { value: 'any', label: 'Match ANY' },
              { value: 'all', label: 'Match ALL' },
            ]}
            onChange={(v) => setMatchMode(v as SearchMatchMode)}
          />
          <Segmented
            label="Exclude terms"
            info="Hide matches: documents containing an exclude term are removed from the results entirely. Flag only: they stay in the results with the matched exclude terms shown, and can be shown/hidden from the results toolbar."
            value={excludeMode}
            options={[
              { value: 'exclude', label: 'Hide matches' },
              { value: 'flag', label: 'Flag only' },
            ]}
            onChange={(v) => setExcludeMode(v as SearchExcludeMode)}
          />
          {!refineDraft && (
            <div>
              <span className="block text-xs font-medium text-gray-400 uppercase mb-1.5">
                Document status
                <InfoTip text='Only documents in this pipeline status are scanned. "completed" (the default) means the document was fully processed and its text extracted — other statuses usually have little or no searchable text.' />
              </span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}
          <label className="flex items-center gap-2 cursor-pointer pb-1.5">
            <input
              type="checkbox"
              checked={wholeWord}
              onChange={(e) => setWholeWord(e.target.checked)}
              className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-900"
            />
            <span className="text-sm text-gray-300">Whole word / phrase only</span>
            <InfoTip text='Match terms only at word boundaries: "race" will not match inside "embraced". Phrases and terms ending in punctuation (like C++) still work. Turn off to allow matches inside longer words.' />
          </label>
        </div>

        <div className="flex items-center gap-3 mt-5">
          <Button onClick={() => handleRun()} disabled={!canRun} loading={phase === 'starting'}>
            {phase === 'running' ? 'Searching…' : refineDraft ? 'Run Refinement' : 'Run Search'}
          </Button>
          {includeTerms.length === 0 && (
            <Text className="text-gray-500 text-xs">Add at least one include term.</Text>
          )}
          {includeTerms.length > 0 && !refineDraft && selectedBatches.size === 0 && (
            <Text className="text-gray-500 text-xs">Select at least one batch.</Text>
          )}
          {matchMode === 'all' && includeTerms.length > 5 && (
            <Text className="text-amber-400/80 text-xs">
              Match ALL with many terms usually returns very few documents.
            </Text>
          )}
        </div>
      </Card>

      {/* ─── Saved searches (shared) ─── */}
      <Card className="bg-gray-900 ring-gray-800">
        <div className="flex items-center justify-between mb-3">
          <Text className="text-gray-200 font-medium">
            Saved searches <span className="text-gray-500 font-normal">(shared with all admins)</span>
            <InfoTip text="Permanent snapshots of search results, visible to every admin. Refinements and filtered sets are saved automatically as indented steps under the search they came from — open any step to view, export, or refine it further. Live (unsaved) results expire after 24 hours; saved ones never do." />
          </Text>
          <Button size="xs" variant="secondary" onClick={() => loadSavedSearches()} className="bg-gray-800 border-gray-700">Refresh</Button>
        </div>
        {savedSearches.length === 0 ? (
          <Text className="text-gray-500 text-sm">
            No saved searches yet. Run a search, then click “Save to shared” to save a snapshot here for all admins.
          </Text>
        ) : (
          <div className="divide-y divide-gray-800/60">
            {savedGroups.map(({ root, steps }) => (
              <Fragment key={root.chainId || root.id}>
                {[root, ...steps].map((s) => {
                  const isStep = s.id !== root.id;
                  return (
                    <div
                      key={s.id}
                      className={`flex items-center justify-between py-2.5 gap-4 ${isStep ? 'pl-6 border-l-2 border-gray-800 ml-2' : ''}`}
                    >
                      <button
                        type="button"
                        onClick={() => openSaved(s.id)}
                        className="flex-1 min-w-0 text-left group"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-gray-100 text-sm font-medium truncate group-hover:text-blue-300">{s.name}</span>
                          {s.kind === 'refine' && <Badge color="amber" size="xs">refined</Badge>}
                          {s.kind === 'derive' && <Badge color="violet" size="xs">filtered</Badge>}
                          {savedView?.id === s.id && <Badge color="indigo" size="xs">viewing</Badge>}
                        </div>
                        <div className="text-gray-500 text-xs truncate">
                          {s.matchCount.toLocaleString()} results · by {formatSavedBy(s.createdBy)} · {new Date(s.createdAt).toLocaleDateString()}
                          {s.durationMs ? ` · scanned in ${formatDurationMs(s.durationMs)}` : ''}
                          {s.refinedFrom?.label ? ` · ${s.refinedFrom.label}` : ''}
                          {s.description ? ` · ${s.description}` : ''}
                        </div>
                      </button>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button size="xs" variant="secondary" onClick={() => openSaved(s.id)} className="bg-gray-800 border-gray-700">Open</Button>
                        <Button size="xs" variant="secondary" onClick={() => api.downloadSavedSearchResults(s.id).catch((e) => setError(e instanceof Error ? e.message : 'Export failed'))} title="Download this snapshot's full results as CSV." className="bg-gray-800 border-gray-700">CSV</Button>
                        <Button size="xs" variant="secondary" onClick={() => handleDeleteSaved(s.id, s.name)} title="Delete this snapshot for all admins. Steps refined from it stay, but lose their jump-back parent." className="bg-gray-800 border-gray-700 text-red-300">Delete</Button>
                      </div>
                    </div>
                  );
                })}
              </Fragment>
            ))}
          </div>
        )}
      </Card>

      {/* ─── Restoring previous search ─── */}
      {phase === 'restoring' && (
        <Card className="bg-gray-900 ring-gray-800">
          <Text className="text-gray-400">Restoring previous search…</Text>
        </Card>
      )}

      {/* ─── Progress ─── */}
      {isRunning && (
        <Card className="bg-gray-900 ring-gray-800">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Text className="text-gray-300">
              Searching {progress.processed.toLocaleString()} / {progress.total.toLocaleString()} documents
              {' · '}
              <span className="text-gray-100">{progress.matchCount.toLocaleString()}</span> matches
              {searchDurationMs != null && searchDurationMs >= 1000 && (
                <span className="text-gray-500"> · {formatDurationMs(searchDurationMs)} elapsed</span>
              )}
            </Text>
            <div className="flex items-center gap-2">
              {phase === 'running' && (
                <Button size="xs" variant="secondary" onClick={handlePause} loading={controlBusy} title="Stop at the next checkpoint, keeping everything found so far. Resume any time." className="bg-gray-800 border-gray-700">
                  Pause
                </Button>
              )}
              <Button size="xs" variant="secondary" onClick={handleCancel} disabled={controlBusy} title="Stop the search for good. Documents found so far are kept and can be viewed, saved, or exported." className="bg-gray-800 border-gray-700 text-red-300">
                Cancel
              </Button>
            </div>
          </div>
          <div className="h-2 bg-gray-800 rounded mt-2 overflow-hidden">
            <div className="h-full bg-blue-500 transition-all duration-300" style={{ width: `${pct}%` }} />
          </div>
          {searchStale && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded border border-amber-800 bg-amber-950/40 px-3 py-2 text-sm text-amber-200">
              <span>This search looks stalled (no progress recently — the server pod may have restarted).</span>
              <Button size="xs" onClick={handleResume} loading={controlBusy} title="Continue scanning from where the search left off — nothing already found is lost." className="bg-amber-600 hover:bg-amber-500 border-0">Resume</Button>
            </div>
          )}
        </Card>
      )}

      {/* ─── Paused ─── */}
      {phase === 'paused' && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-800 bg-amber-950/40 px-4 py-3">
          <Text className="text-amber-200">
            Paused — <span className="font-semibold">{progress.matchCount.toLocaleString()}</span> matched so far
            {' '}<span className="text-amber-300/70">(scanned {progress.processed.toLocaleString()} of {progress.total.toLocaleString()}{searchDurationMs ? ` in ${formatDurationMs(searchDurationMs)}` : ''})</span>. Showing results found so far.
          </Text>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="xs" onClick={handleResume} loading={controlBusy} title="Continue scanning from where the search left off — nothing already found is lost." className="bg-amber-600 hover:bg-amber-500 border-0">Resume</Button>
            <Button size="xs" variant="secondary" onClick={startRefine} loading={refinePreparing} disabled={resultsTotal === 0} title="Search within these results: add narrower terms and run them over just these documents. The current results are saved as a permanent snapshot first." className="bg-gray-800 border-gray-700">Refine</Button>
            <Button size="xs" variant="secondary" onClick={handleExport} loading={exporting} disabled={resultsTotal === 0} title="Download the results as CSV. Active view filters (term chips, exclude filter) apply." className="bg-gray-800 border-gray-700">Export CSV</Button>
            <Button size="xs" variant="secondary" onClick={() => { setSaveName(''); setSaveDescription(''); setSaveError(null); setShowSaveModal(true); }} disabled={resultsTotal === 0} title="Save everything found so far as a permanent named snapshot all admins can open." className="bg-gray-800 border-gray-700">Save</Button>
            <Button size="xs" variant="secondary" onClick={handleCancel} disabled={controlBusy} className="bg-gray-800 border-gray-700 text-red-300">Cancel</Button>
          </div>
        </div>
      )}

      {/* ─── Cancelled ─── */}
      {phase === 'cancelled' && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-700 bg-gray-900 px-4 py-3">
          <Text className="text-gray-300">
            Cancelled — <span className="font-semibold">{progress.matchCount.toLocaleString()}</span> documents matched before stopping
            {' '}<span className="text-gray-500">(scanned {progress.processed.toLocaleString()} of {progress.total.toLocaleString()}{searchDurationMs ? ` in ${formatDurationMs(searchDurationMs)}` : ''})</span>. The partial results below are kept.
          </Text>
          <div className="flex items-center gap-2">
            <Button size="xs" variant="secondary" onClick={startRefine} loading={refinePreparing} disabled={resultsTotal === 0} title="Search within these results: add narrower terms and run them over just these documents. The current results are saved as a permanent snapshot first." className="bg-gray-800 border-gray-700">Refine</Button>
            <Button size="xs" variant="secondary" onClick={handleExport} loading={exporting} disabled={resultsTotal === 0} title="Download the results as CSV. Active view filters (term chips, exclude filter) apply." className="bg-gray-800 border-gray-700">Export CSV</Button>
            <Button size="xs" variant="secondary" onClick={() => { setSaveName(''); setSaveDescription(''); setSaveError(null); setShowSaveModal(true); }} disabled={resultsTotal === 0} title="Save everything found so far as a permanent named snapshot all admins can open." className="bg-gray-800 border-gray-700">Save</Button>
          </div>
        </div>
      )}

      {/* ─── Failure ─── */}
      {phase === 'failed' && (
        <div className="space-y-3">
          <ErrorBanner error={error} />
          <Button variant="secondary" onClick={() => handleRun()} disabled={!canRun} className="bg-gray-800 border-gray-700">
            Retry
          </Button>
        </div>
      )}

      {/* ─── Completion banner ─── */}
      {phase === 'complete' && (
        <div className="space-y-2">
          {warning && (
            <div className="rounded-lg border border-amber-800 bg-amber-950/40 px-4 py-2 text-sm text-amber-200">
              {warning}
            </div>
          )}
          {cappedAt != null && (
            <div className="rounded-lg border border-amber-800 bg-amber-950/40 px-4 py-2 text-sm text-amber-200">
              Results truncated — only the first {cappedAt.toLocaleString()} documents were scanned.
            </div>
          )}

          {mode === 'saved' && savedView ? (
            /* Saved snapshot view */
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-indigo-800 bg-indigo-950/40 px-4 py-3">
              <Text className="text-indigo-100">
                Saved search: <span className="font-semibold">{savedView.name}</span>
                {savedView.kind === 'refine' && <Badge color="amber" size="xs" className="ml-2">refined</Badge>}
                {savedView.kind === 'derive' && <Badge color="violet" size="xs" className="ml-2">filtered</Badge>}
                {' '}<span className="text-indigo-300/70">
                  — {savedView.matchCount.toLocaleString()} results · by {formatSavedBy(savedView.createdBy)} · {new Date(savedView.createdAt).toLocaleDateString()}
                  {savedView.durationMs ? ` · scanned in ${formatDurationMs(savedView.durationMs)}` : ''}
                  {savedView.refinedFromLabel ? ` · ${savedView.refinedFromLabel}` : ''}
                </span>
              </Text>
              <div className="flex items-center gap-2">
                <Button onClick={startRefine} loading={refinePreparing} disabled={resultsTotal === 0} title="Search within these results: add narrower terms and run them over just these documents. The new result set is auto-saved as the next step of this chain." className="bg-amber-600 hover:bg-amber-500 border-0">
                  Refine
                </Button>
                {/* Re-run only for root searches: a refined/derived step's corpus
                    was another snapshot, so re-running it against raw batches
                    would silently change what it means. */}
                {savedView.kind === 'search' && (
                  <Button onClick={() => handleRun({ id: savedView.id, name: savedView.name })} disabled={!canRun} title="Run this saved query again against its batches — picks up documents processed since the snapshot was taken. You can then overwrite the snapshot with the fresh results." className="bg-indigo-600 hover:bg-indigo-500 border-0">
                    Re-run
                  </Button>
                )}
                <Button onClick={handleExport} loading={exporting} disabled={resultsTotal === 0} title="Download the results as CSV. Active view filters (term chips, exclude filter) apply." variant="secondary" className="bg-gray-800 border-gray-700">
                  Export CSV
                </Button>
              </div>
            </div>
          ) : (
            /* Live search complete */
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-800 bg-emerald-950/40 px-4 py-3">
                <Text className="text-emerald-200">
                  Search complete — <span className="font-semibold">{progress.matchCount.toLocaleString()}</span> documents matched
                  {' '}<span className="text-emerald-300/70">(scanned {progress.processed.toLocaleString()}{searchDurationMs ? ` in ${formatDurationMs(searchDurationMs)}` : ''})</span>
                  {autoSavedId && <span className="text-emerald-300/70"> · auto-saved to the chain</span>}
                </Text>
                <div className="flex items-center gap-2">
                  {rerunOfSaved && (
                    <Button onClick={handleUpdateSaved} loading={saving} title="Overwrite the saved snapshot with these fresh results (its query and name stay the same)." className="bg-indigo-600 hover:bg-indigo-500 border-0">
                      Update “{rerunOfSaved.name}”
                    </Button>
                  )}
                  <Button onClick={startRefine} loading={refinePreparing} disabled={resultsTotal === 0} title="Search within these results: add narrower terms and run them over just these documents. These results are saved as a permanent snapshot first, and each refinement is auto-saved as a step you can jump back to." className="bg-amber-600 hover:bg-amber-500 border-0">
                    Refine these results
                  </Button>
                  <Button
                    onClick={() => { setSaveName(''); setSaveDescription(''); setSaveError(null); setShowSaveModal(true); }}
                    disabled={resultsTotal === 0}
                    variant="secondary"
                    title="Save these results as a permanent named snapshot all admins can open. Live results expire after 24 hours; saved ones never do."
                    className="bg-gray-800 border-gray-700"
                  >
                    Save to shared
                  </Button>
                  <Button
                    onClick={handleExport}
                    loading={exporting}
                    disabled={resultsTotal === 0}
                    title="Download the results as CSV. Active view filters (term chips, exclude filter) apply."
                    className="bg-emerald-600 hover:bg-emerald-500 border-0"
                  >
                    Export Results CSV
                  </Button>
                </div>
              </div>
              {autoSaveError && (
                <div className="rounded-lg border border-amber-800 bg-amber-950/40 px-4 py-2 text-sm text-amber-200">
                  Auto-save of this refinement failed ({autoSaveError}) — use “Save to shared” to keep it.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── Refinement chain breadcrumb ─── */}
      {hasResults && chainSteps.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5 px-1">
          <span className="text-gray-500 text-xs uppercase font-medium mr-1">
            Chain
            <InfoTip text="Your refinement history: the original search, then each refinement or filter step, with its result count. Every step is a permanent snapshot — click one to jump back to those results, then refine again from there." />
          </span>
          {chainSteps.map((step, i) => {
            const isCurrent = (mode === 'saved' ? savedView?.id : autoSavedId) === step.id;
            return (
              <Fragment key={step.id}>
                {i > 0 && <span className="text-gray-600 text-sm">→</span>}
                <button
                  type="button"
                  onClick={() => { if (!isCurrent) openSaved(step.id); }}
                  title={`${step.name} · ${step.matchCount.toLocaleString()} results${step.refinedFrom?.label ? ` · ${step.refinedFrom.label}` : ''}`}
                  className={`px-2.5 py-1 rounded-full text-xs ring-1 transition-colors ${
                    isCurrent
                      ? 'bg-indigo-900/70 text-indigo-100 ring-indigo-500'
                      : 'bg-gray-900 text-gray-300 ring-gray-700 hover:ring-gray-500 hover:text-gray-100'
                  }`}
                >
                  {step.matchCount.toLocaleString()}
                  <span className={isCurrent ? 'text-indigo-300/80 ml-1' : 'text-gray-500 ml-1'}>
                    {step.kind === 'derive' ? 'filtered' : step.kind === 'refine' ? 'refined' : 'search'}
                  </span>
                </button>
              </Fragment>
            );
          })}
        </div>
      )}

      {/* ─── Results table ─── */}
      {hasResults && (
        <Card className="bg-gray-900 ring-gray-800">
          {/* Found-term filter chips: narrow the view to docs that matched ALL selected terms */}
          {termStats && termStats.includeTerms.length > 1 && (
            <TermChipBar
              stats={termStats.includeTerms}
              required={requiredTerms}
              onToggle={toggleRequiredTerm}
              onClear={clearRequiredTerms}
              onMaterialize={handleMaterialize}
              materializing={materializing}
              materializeCount={resultsTotal}
            />
          )}
          {/* Toolbar: exclude-term filter (left) · snippet toggle + column picker (right) */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="flex flex-wrap items-center gap-3">
              {excludeMatchTotal > 0 && (
                <label className="flex items-center gap-1.5 text-gray-400 text-sm">
                  <span>Rows with an excluded term</span>
                  <InfoTip text='These documents also matched one of your exclude terms (you searched in "Flag only" mode). Show them, hide them, or show only them — the CSV export follows whatever is on screen.' />
                  <select
                    value={excludeFilter}
                    onChange={(e) => changeExcludeFilter(e.target.value as SearchExcludeFilter)}
                    className="bg-gray-950 border border-gray-800 rounded px-2 py-1 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="all">Show ({excludeMatchTotal.toLocaleString()})</option>
                    <option value="without">Hide</option>
                    <option value="with">Only</option>
                  </select>
                </label>
              )}
              {selectionEnabled && (
                <>
                  <Text className="text-gray-400 text-sm">
                    {selectedIds.size.toLocaleString()} of {allIds.length.toLocaleString()} selected
                    <InfoTip text="Selection for the Vectara export, using the checkboxes in the table. It follows the active filters — narrowing by term or exclude-filter reselects just the visible set. The header checkbox selects/clears every page at once." />
                  </Text>
                  <Button
                    size="xs"
                    disabled={selectedIds.size === 0}
                    onClick={openVectaraModal}
                    title="Index the selected documents into a Vectara corpus for semantic (meaning-based) search, beyond exact term matching."
                    className="bg-violet-600 hover:bg-violet-500 border-0"
                  >
                    Export to Vectara ({selectedIds.size.toLocaleString()})
                  </Button>
                  {vexportId && !showVectaraModal && (vexportPhase === 'running' || vexportStale) && (
                    <button
                      type="button"
                      onClick={() => setShowVectaraModal(true)}
                      title={vexportStale
                        ? 'The export was interrupted (server restart) — open to resume from its checkpoint.'
                        : 'A Vectara export is running in the background — open to see progress or cancel.'}
                      className={`rounded-full px-2.5 py-0.5 text-xs ring-1 ${
                        vexportStale
                          ? 'bg-amber-950/40 text-amber-200 ring-amber-800 hover:bg-amber-900/40'
                          : 'bg-violet-950/40 text-violet-200 ring-violet-800 hover:bg-violet-900/40'
                      }`}
                    >
                      {vexportStale
                        ? 'Vectara export interrupted — Resume?'
                        : `Vectara export ${(vexportStatus?.processed ?? 0).toLocaleString()}/${(vexportStatus?.total ?? 0).toLocaleString()}`}
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
            {isColumnVisible('snippet') && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={snippetExpanded}
                  onChange={toggleSnippetExpanded}
                  className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-900"
                />
                <span className="text-sm text-gray-300">Full snippet rows</span>
                <InfoTip text="Show each result's full matching sentence in a wide row beneath it, instead of a truncated snippet cell. Easier to read long matches." />
              </label>
            )}
            <div className="relative inline-flex" ref={columnPickerRef}>
              <Button
                size="xs"
                variant="secondary"
                onClick={() => setShowColumnPicker((s) => !s)}
                className="bg-gray-800 border-gray-700 shrink-0 whitespace-nowrap"
                title="Choose which columns are shown in the results table (remembered in this browser)."
              >
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Columns
                </span>
              </Button>
              {showColumnPicker && (
                <div className="absolute right-0 mt-2 w-52 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-50">
                  <div className="py-2">
                    <div className="px-3 py-1 text-xs font-medium text-gray-400 uppercase">Columns</div>
                    {RESULT_COLUMNS.map((col) => {
                      const isLast = visibleColumns.size === 1 && visibleColumns.has(col.key);
                      return (
                        <label
                          key={col.key}
                          className={`flex items-center px-3 py-1.5 hover:bg-gray-700 ${isLast ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <input
                            type="checkbox"
                            checked={visibleColumns.has(col.key)}
                            disabled={isLast}
                            onChange={() => toggleColumn(col.key)}
                            className="mr-2 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-800"
                          />
                          <span className="text-sm text-gray-300">{col.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            </div>
          </div>

          <div className={`overflow-x-auto${resultsLoading ? ' opacity-50 pointer-events-none' : ''}`}>
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  {selectionEnabled && (
                    <th className="py-3 px-4 w-10">
                      <input
                        type="checkbox"
                        aria-label="Select all results"
                        checked={allSelected}
                        ref={(el) => { if (el) el.indeterminate = someSelected; }}
                        onChange={() => (allSelected ? clearSelection() : selectAll())}
                        className="rounded border-gray-600 bg-gray-700 text-violet-500 focus:ring-violet-500 focus:ring-offset-gray-900"
                      />
                    </th>
                  )}
                  {inlineColumns.map((c) => (
                    <th key={c.key} className="text-left py-3 px-4 text-gray-400 text-sm font-medium">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.length === 0 ? (
                  <tr>
                    <td colSpan={totalColSpan} className="py-12 text-center">
                      <Text className="text-gray-500">No documents matched.</Text>
                    </td>
                  </tr>
                ) : (
                  results.map((r) => (
                    <Fragment key={r.documentId}>
                    <tr
                      onClick={(e) => handleRowClick(e, r.documentId)}
                      onAuxClick={(e) => handleRowAuxClick(e, r.documentId)}
                      onMouseDown={handleRowMouseDown}
                      title="Click to open · ⌘/Ctrl/middle-click to open in a new tab"
                      className={`hover:bg-gray-800/50 cursor-pointer transition-colors${snippetAsRow ? '' : ' border-b border-gray-800/50'}`}
                    >
                      {selectionEnabled && (
                        <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            aria-label="Select document"
                            checked={selectedIds.has(r.documentId)}
                            onChange={() => toggleSelect(r.documentId)}
                            className="rounded border-gray-600 bg-gray-700 text-violet-500 focus:ring-violet-500 focus:ring-offset-gray-900"
                          />
                        </td>
                      )}
                      {isColumnVisible('fileName') && (
                        <td className="py-3 px-4">
                          <Text className="text-gray-200 truncate max-w-xs" title={r.sourcePath ? `${r.sourcePath}/${r.fileName}` : r.fileName}>
                            {r.fileName}
                          </Text>
                        </td>
                      )}
                      {isColumnVisible('batch') && (
                        <td className="py-3 px-4">
                          <Text className="text-gray-400 truncate max-w-[12rem]" title={r.batchName || undefined}>
                            {r.batchName || '--'}
                          </Text>
                        </td>
                      )}
                      {isColumnVisible('status') && (
                        <td className="py-3 px-4">
                          <Badge color={statusColor(r.status)} size="sm">{r.status}</Badge>
                        </td>
                      )}
                      {isColumnVisible('genre') && (
                        <td className="py-3 px-4">
                          {r.genreLabel ? (
                            <Badge color="indigo" size="sm">{formatGenre(r.genreLabel)}</Badge>
                          ) : (
                            <Text className="text-gray-500">--</Text>
                          )}
                        </td>
                      )}
                      {isColumnVisible('labels') && (
                        <td className="py-3 px-4">
                          {r.harmfulCount > 0 ? (
                            <Badge color="red" size="sm">{r.harmfulCount} found</Badge>
                          ) : (
                            <Badge color="emerald" size="sm">Clean</Badge>
                          )}
                        </td>
                      )}
                      {isColumnVisible('include') && (
                        <td className="py-3 px-4">
                          <TermPills terms={r.matchedIncludeTerms} color="blue" />
                        </td>
                      )}
                      {isColumnVisible('exclude') && (
                        <td className="py-3 px-4">
                          {r.matchedExcludeTerms.length > 0
                            ? <TermPills terms={r.matchedExcludeTerms} color="amber" />
                            : <Text className="text-gray-600">--</Text>}
                        </td>
                      )}
                      {isColumnVisible('snippet') && !snippetExpanded && (
                        <td className="py-3 px-4">
                          <Text className="text-gray-400 text-sm truncate max-w-md" title={r.snippet}>
                            {r.snippet || '--'}
                          </Text>
                        </td>
                      )}
                    </tr>
                    {snippetAsRow && (
                      <tr
                        onClick={(e) => handleRowClick(e, r.documentId)}
                        onAuxClick={(e) => handleRowAuxClick(e, r.documentId)}
                        onMouseDown={handleRowMouseDown}
                        className="border-b border-gray-800/50 hover:bg-gray-800/40 cursor-pointer transition-colors"
                      >
                        <td colSpan={totalColSpan} className="px-4 pb-3 -mt-1">
                          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Snippet</div>
                          <Text className="text-gray-300 text-sm whitespace-pre-wrap break-words">
                            {r.snippetFull || r.snippet || '--'}
                          </Text>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-800">
            <div className="flex items-center gap-3">
              <Text className="text-gray-400 text-sm">
                {resultsTotal === 0
                  ? (excludeFilter === 'all' ? 'No results' : 'No results match this filter')
                  : `Showing ${((resultsPage - 1) * pageSize) + 1} - ${Math.min(resultsPage * pageSize, resultsTotal)} of ${resultsTotal.toLocaleString()}${excludeFilter === 'all' ? '' : ' (filtered)'}`}
              </Text>
              <label className="flex items-center gap-1.5 text-gray-400 text-sm">
                <span>Rows</span>
                <select
                  value={pageSize}
                  onChange={(e) => changePageSize(Number(e.target.value))}
                  className="bg-gray-950 border border-gray-800 rounded px-2 py-1 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="xs"
                variant="secondary"
                disabled={resultsPage <= 1}
                onClick={() => setResultsPage((p) => p - 1)}
                className="bg-gray-800 border-gray-700"
              >
                Previous
              </Button>
              <Text className="text-gray-400 text-sm px-2">Page {resultsPage} of {totalPages}</Text>
              <Button
                size="xs"
                variant="secondary"
                disabled={resultsPage >= totalPages}
                onClick={() => setResultsPage((p) => p + 1)}
                className="bg-gray-800 border-gray-700"
              >
                Next
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* ─── Save-to-shared modal ─── */}
      <Dialog open={showSaveModal} onClose={() => setShowSaveModal(false)} static={true}>
        <DialogPanel className="bg-gray-900 border border-gray-800 max-w-md">
          <Text className="text-gray-100 text-lg font-semibold">Save search to shared</Text>
          <Text className="text-gray-400 text-sm mt-1">
            Saves a snapshot of these {resultsTotal.toLocaleString()} results, visible to all admins.
          </Text>
          <div className="mt-4 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-400 uppercase mb-1">Name</label>
              <TextInput
                placeholder="e.g. Redlining sweep — Jan batches"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 uppercase mb-1">Description <span className="text-gray-600 normal-case">(optional)</span></label>
              <TextInput
                placeholder="What this search is for"
                value={saveDescription}
                onChange={(e) => setSaveDescription(e.target.value)}
              />
            </div>
            {saveError && (
              <div className="rounded ring-1 ring-rose-800 bg-rose-950/40 px-3 py-2 text-sm text-rose-200">
                <span className="font-semibold">Couldn’t save.</span>{' '}
                <span className="text-rose-300/90">{saveError}</span>
              </div>
            )}
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowSaveModal(false)} className="bg-gray-800 border-gray-700">
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving} disabled={!saveName.trim()}>
              Save
            </Button>
          </div>
        </DialogPanel>
      </Dialog>

      {/* ─── Export-to-Vectara modal ─── */}
      <Dialog open={showVectaraModal} onClose={() => setShowVectaraModal(false)} static={true}>
        <DialogPanel className="bg-gray-900 border border-gray-800 max-w-lg">
          <Text className="text-gray-100 text-lg font-semibold">Export to Vectara</Text>

          {vexportPhase === 'idle' ? (
            <>
              <Text className="text-gray-400 text-sm mt-1">
                Index {selectedIds.size.toLocaleString()} selected document{selectedIds.size === 1 ? '' : 's'} into a Vectara
                corpus. Each document carries metadata with a link back to its console page.
              </Text>

              <div className="mt-4 space-y-4">
                {/* Corpus */}
                <div>
                  <span className="block text-xs font-medium text-gray-400 uppercase mb-1.5">Corpus</span>
                  <div className="flex gap-4 mb-2">
                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input type="radio" checked={corpusMode === 'existing'} onChange={() => setCorpusMode('existing')} /> Existing
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input type="radio" checked={corpusMode === 'new'} onChange={() => setCorpusMode('new')} /> Create new
                    </label>
                  </div>
                  {corpusMode === 'existing' ? (
                    corporaLoading ? (
                      <Text className="text-gray-500 text-sm">Loading corpora…</Text>
                    ) : corpora.length === 0 ? (
                      <Text className="text-gray-500 text-sm">No corpora yet — create one.</Text>
                    ) : (
                      <select
                        value={selectedCorpusKey}
                        onChange={(e) => setSelectedCorpusKey(e.target.value)}
                        className="w-full bg-gray-950 border border-gray-800 rounded px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-violet-500"
                      >
                        {corpora.map((c) => <option key={c.key} value={c.key}>{c.name} ({c.key})</option>)}
                      </select>
                    )
                  ) : (
                    <div className="space-y-2">
                      <TextInput placeholder="corpus key (e.g. harmcheck-export)" value={newCorpus.key} onChange={(e) => setNewCorpus((n) => ({ ...n, key: e.target.value }))} />
                      <TextInput placeholder="display name (optional)" value={newCorpus.name} onChange={(e) => setNewCorpus((n) => ({ ...n, name: e.target.value }))} />
                      <TextInput placeholder="description (optional)" value={newCorpus.description} onChange={(e) => setNewCorpus((n) => ({ ...n, description: e.target.value }))} />
                    </div>
                  )}
                </div>

                {/* Content type */}
                <div>
                  <span className="block text-xs font-medium text-gray-400 uppercase mb-1.5">Index</span>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input type="radio" checked={vContentType === 'text'} onChange={() => setVContentType('text')} /> Extracted text
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input type="radio" checked={vContentType === 'file'} onChange={() => setVContentType('file')} /> Original file
                    </label>
                  </div>
                  <Text className="text-gray-500 text-xs mt-1">
                    {vContentType === 'text'
                      ? 'Indexes our cleaned OCR text (fast, no re-OCR; what your search matched).'
                      : 'Uploads the original file; Vectara re-extracts it (slower, higher fidelity).'}
                  </Text>
                </div>

                {/* Chunking — drives how Vectara embeds (speed vs match granularity) */}
                <div>
                  <span className="block text-xs font-medium text-gray-400 uppercase mb-1.5">Chunking</span>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input type="radio" checked={vChunking === 'sentence'} onChange={() => setVChunking('sentence')} /> Sentence
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input type="radio" checked={vChunking === 'max_chars'} onChange={() => setVChunking('max_chars')} /> 1024-char chunks
                    </label>
                  </div>
                  <Text className="text-gray-500 text-xs mt-1">
                    {vChunking === 'sentence'
                      ? 'One embedding per sentence — most precise matching, but slow to index (a minute or more per large document).'
                      : '~5–10× faster indexing; query matches return ~1 KB passages instead of single sentences. Documents already in the corpus keep their old chunking unless Overwrite is on.'}
                  </Text>
                </div>

                {/* Overwrite */}
                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={vOverwrite}
                    onChange={(e) => setVOverwrite(e.target.checked)}
                    className="rounded border-gray-600 bg-gray-700 text-violet-500 focus:ring-violet-500 focus:ring-offset-gray-900"
                  />
                  Overwrite documents already in the corpus
                </label>

                {vModalError && (
                  <div className="rounded ring-1 ring-rose-800 bg-rose-950/40 px-3 py-2 text-sm text-rose-200">{vModalError}</div>
                )}
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setShowVectaraModal(false)} className="bg-gray-800 border-gray-700">Cancel</Button>
                <Button
                  onClick={handleStartVectaraExport}
                  loading={vStarting}
                  disabled={selectedIds.size === 0
                    || (corpusMode === 'existing' && !selectedCorpusKey)
                    || (corpusMode === 'new' && !newCorpus.key.trim())}
                  className="bg-violet-600 hover:bg-violet-500 border-0"
                >
                  Export {selectedIds.size.toLocaleString()}
                </Button>
              </div>
            </>
          ) : (
            (() => {
              const s = vexportStatus;
              const total = s?.total ?? selectedIds.size;
              const processed = s?.processed ?? 0;
              const pctV = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
              const interrupted = vexportPhase === 'running' && vexportStale;
              return (
                <div className="mt-3">
                  <Text className="text-gray-300">
                    {vexportPhase === 'complete' ? 'Export complete'
                      : vexportPhase === 'failed' ? 'Export failed'
                      : vexportPhase === 'cancelled' ? 'Export cancelled'
                      : interrupted ? 'Export interrupted'
                      : 'Exporting…'}
                    {' — '}{processed.toLocaleString()} / {total.toLocaleString()}
                    {(s?.durationMs ?? 0) >= 1000 && (
                      <span className="text-gray-500"> · {formatDurationMs(s!.durationMs!)}{vexportPhase === 'running' && !interrupted ? ' elapsed' : ''}</span>
                    )}
                  </Text>
                  <div className="h-2 bg-gray-800 rounded mt-2 overflow-hidden">
                    <div
                      className={`h-full ${vexportPhase === 'failed' ? 'bg-rose-500' : vexportPhase === 'cancelled' || interrupted ? 'bg-amber-500' : 'bg-violet-500'} transition-all duration-300${vexportPhase === 'running' && !interrupted && processed === 0 ? ' animate-pulse' : ''}`}
                      style={{ width: `${vexportPhase === 'running' && processed === 0 ? 8 : pctV}%` }}
                    />
                  </div>
                  <div className="flex gap-4 mt-3 text-sm">
                    <span className="text-emerald-300">{(s?.succeeded ?? 0).toLocaleString()} succeeded</span>
                    <span className="text-amber-300">{(s?.skipped ?? 0).toLocaleString()} skipped</span>
                    <span className="text-rose-300">{(s?.failed ?? 0).toLocaleString()} failed</span>
                  </div>
                  {interrupted && (
                    <div className="rounded ring-1 ring-amber-800 bg-amber-950/40 px-3 py-2 text-sm text-amber-200 mt-3">
                      The export stopped reporting progress — likely a server restart mid-export. Resume continues
                      from the last checkpoint; nothing already indexed is lost.
                    </div>
                  )}
                  {vexportPhase === 'running' && !interrupted && (
                    <Text className="text-gray-500 text-xs mt-2">
                      The export runs on the server — you can close this dialog and keep working; progress stays
                      visible next to the “Export to Vectara” button. Sentence chunking can take a minute+ per
                      large document.
                    </Text>
                  )}
                  {vModalError && (
                    <div className="rounded ring-1 ring-rose-800 bg-rose-950/40 px-3 py-2 text-sm text-rose-200 mt-3">{vModalError}</div>
                  )}
                  {s?.error && <Text className="text-rose-300 text-sm mt-2">{s.error}</Text>}
                  {!!s?.failures?.length && (
                    <div className="mt-3 max-h-40 overflow-y-auto rounded border border-gray-800 bg-gray-950 p-2 text-xs space-y-0.5">
                      <div className="text-rose-300 mb-1">Failures (first {s.failures.length}):</div>
                      {s.failures.map((f) => (
                        <div key={f.documentId} className="text-gray-400 truncate" title={`${f.documentId}: ${f.error}`}>
                          <span className="text-gray-300">{f.documentId}</span>: {f.error}
                        </div>
                      ))}
                    </div>
                  )}
                  {!!s?.skips?.length && (
                    <div className="mt-3 max-h-40 overflow-y-auto rounded border border-gray-800 bg-gray-950 p-2 text-xs space-y-0.5">
                      <div className="text-amber-300/80 mb-1">Skipped (first {s.skips.length}) — expected conditions, nothing to fix:</div>
                      {s.skips.map((k) => (
                        <div key={k.documentId} className="text-gray-500 truncate" title={`${k.documentId}: ${k.reason}`}>
                          <span className="text-gray-400">{k.documentId}</span>: {k.reason}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-6 flex justify-end gap-2">
                    {interrupted && (
                      <Button
                        onClick={handleVectaraResume}
                        title="Continue exporting from the last checkpoint — already-indexed documents are skipped or overwritten safely."
                        className="bg-amber-600 hover:bg-amber-500 border-0"
                      >
                        Resume
                      </Button>
                    )}
                    {vexportPhase === 'running' && (
                      <Button
                        variant="secondary"
                        onClick={handleVectaraCancel}
                        title="Stop the export. Documents already indexed stay in the corpus."
                        className="bg-gray-800 border-gray-700 text-rose-300"
                      >
                        Cancel export
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      onClick={() => setShowVectaraModal(false)}
                      className="bg-gray-800 border-gray-700"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              );
            })()
          )}
        </DialogPanel>
      </Dialog>
    </div>
  );
}

// ─── Sub-components ───

// Small hover/focus "i" tooltip for explaining a control in place. Uses a
// named group so it can sit inside elements that already use `group`, and
// prevents default so it's safe inside <label> without toggling the control.
function InfoTip({ text }: { text: string }) {
  return (
    <span className="group/info relative inline-flex align-middle ml-1.5">
      <span
        tabIndex={0}
        role="note"
        aria-label={text}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        className="w-3.5 h-3.5 inline-flex items-center justify-center rounded-full ring-1 ring-gray-600 text-gray-500 hover:text-gray-200 hover:ring-gray-400 focus:text-gray-200 focus:ring-gray-400 focus:outline-none text-[10px] leading-none italic cursor-help select-none"
      >
        i
      </span>
      <span className="pointer-events-none absolute left-0 top-full mt-1.5 z-50 hidden w-64 group-hover/info:block group-focus-within/info:block rounded-lg bg-gray-800 ring-1 ring-gray-600 px-3 py-2 text-xs leading-relaxed text-gray-200 shadow-xl text-left normal-case font-normal tracking-normal whitespace-normal">
        {text}
      </span>
    </span>
  );
}

interface SegmentedProps {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  info?: string;
}

function Segmented({ label, value, options, onChange, info }: SegmentedProps) {
  return (
    <div>
      <span className="block text-xs font-medium text-gray-400 uppercase mb-1.5">
        {label}
        {info && <InfoTip text={info} />}
      </span>
      <div className="inline-flex rounded border border-gray-800 bg-gray-950 p-0.5 gap-0.5">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`px-3 py-1.5 text-sm rounded transition-colors ${
              value === opt.value ? 'bg-blue-900/60 text-blue-100' : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Found-term filter chips: each chip is a search line with its matched-doc
// count; selecting chips narrows the view to docs that matched ALL of them
// (instant — a query over stored results, no document re-scan).
const TERM_CHIP_LIMIT = 30;

interface TermChipBarProps {
  stats: { term: string; count: number }[];
  required: string[];
  onToggle: (term: string) => void;
  onClear: () => void;
  onMaterialize: () => void;
  materializing: boolean;
  materializeCount: number;
}

function TermChipBar({ stats, required, onToggle, onClear, onMaterialize, materializing, materializeCount }: TermChipBarProps) {
  const [showAll, setShowAll] = useState(false);
  // Keep selected chips visible even when they fall outside the display cap.
  const shown = showAll ? stats : stats.filter(
    (s, i) => i < TERM_CHIP_LIMIT || required.includes(s.term),
  );
  const hidden = stats.length - shown.length;

  return (
    <div className="flex flex-wrap items-center gap-1.5 mb-3 pb-3 border-b border-gray-800">
      <span className="text-gray-400 text-sm mr-1">
        Narrow by matched term{required.length > 0 ? ` (${required.length} required)` : ''}:
        <InfoTip text='Each chip is one of your search lines, with how many result documents matched it. Select chips to show only documents that matched ALL of them — e.g. select "apple" and "banana" to see only documents containing both. Instant: it filters the results you already have, nothing is re-scanned. "Save as step" keeps the filtered view as a permanent snapshot.' />
      </span>
      {shown.map(({ term, count }) => {
        const active = required.includes(term);
        return (
          <button
            key={term}
            type="button"
            onClick={() => onToggle(term)}
            title={active ? 'Click to stop requiring this term' : 'Click to show only documents that also matched this term'}
            className={`px-2 py-0.5 rounded-full text-xs ring-1 transition-colors ${
              active
                ? 'bg-blue-900/70 text-blue-100 ring-blue-500'
                : 'bg-gray-950 text-gray-300 ring-gray-700 hover:ring-gray-500 hover:text-gray-100'
            }`}
          >
            {term} <span className={active ? 'text-blue-300/80' : 'text-gray-500'}>({count.toLocaleString()})</span>
          </button>
        );
      })}
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="text-gray-500 hover:text-gray-300 text-xs"
        >
          +{hidden} more
        </button>
      )}
      {required.length > 0 && (
        <>
          <button type="button" onClick={onClear} className="text-blue-400 hover:text-blue-300 text-xs ml-1">
            Clear term filter
          </button>
          <button
            type="button"
            onClick={onMaterialize}
            disabled={materializing}
            title="Save this filtered view as a durable chain step you can refine, export, or share"
            className="text-violet-300 hover:text-violet-200 text-xs ml-2 disabled:opacity-50"
          >
            {materializing ? 'Saving…' : `Save as step (${materializeCount.toLocaleString()})`}
          </button>
        </>
      )}
    </div>
  );
}

function TermPills({ terms, color }: { terms: string[]; color: 'blue' | 'amber' }) {
  if (!terms || terms.length === 0) return <Text className="text-gray-600">--</Text>;
  const shown = terms.slice(0, 5);
  return (
    <div className="flex flex-wrap gap-1" title={terms.join(', ')}>
      {shown.map((t) => (
        <Badge key={t} color={color} size="xs">{t}</Badge>
      ))}
      {terms.length > shown.length && (
        <span className="text-gray-500 text-xs self-center">+{terms.length - shown.length}</span>
      )}
    </div>
  );
}

interface BatchMultiSelectProps {
  batches: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}

function BatchMultiSelect({ batches, selected, onChange }: BatchMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggle = (name: string) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name); else next.add(name);
    onChange(next);
  };

  const summary = selected.size === 0
    ? 'Select batches…'
    : selected.size === 1
      ? [...selected][0]
      : `${selected.size} batches selected`;

  return (
    <div className="relative inline-block w-full max-w-xl" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between bg-gray-950 border border-gray-800 rounded px-3 py-2 text-sm text-left focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        <span className={selected.size === 0 ? 'text-gray-500' : 'text-gray-100 truncate'}>{summary}</span>
        <span className="text-gray-500 text-xs ml-2">▾</span>
      </button>
      {open && (
        <div className="absolute left-0 mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-50">
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700">
            <button type="button" onClick={() => onChange(new Set(batches))} className="text-xs text-blue-400 hover:text-blue-300">
              Select all
            </button>
            <button type="button" onClick={() => onChange(new Set())} className="text-xs text-gray-400 hover:text-gray-200">
              Clear
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {batches.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No batches available</div>
            ) : (
              batches.map((name) => (
                <label key={name} className="flex items-center px-3 py-1.5 hover:bg-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.has(name)}
                    onChange={() => toggle(name)}
                    className="mr-2 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-800"
                  />
                  <span className="text-sm text-gray-300 truncate" title={name}>{name}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
